import {
  BookingQuery,
  BOOKING_QUERY_KEYS,
  DEFAULT_TIMEZONE,
  MAX_EXTRAS,
  MAX_RECEIVERS,
  isValidDate,
  isValidId,
  isValidTime,
  isValidTimezone,
  isValidExtra,
} from "./bookingQuerySchema";

/**
 * Parse a query string into a validated BookingQuery object.
 *
 * @param search - URL search string (with or without leading '?')
 * @returns Validated BookingQuery object with timezone defaulted to Europe/Berlin
 *
 * @example
 * ```ts
 * const query = parseBookingQuery("?locationId=loc-123&pickupDate=2025-12-10");
 * // { locationId: "loc-123", pickupDate: "2025-12-10", timezone: "Europe/Berlin" }
 * ```
 *
 * @remarks
 * - Invalid parameters are silently ignored
 * - Arrays (receivers, extras) are truncated to max limits
 * - Timezone defaults to Europe/Berlin if missing or invalid
 */
export function parseBookingQuery(search: string): BookingQuery {
  const normalizedSearch = search.startsWith("?") ? search.substring(1) : search;
  const params = new URLSearchParams(normalizedSearch);
  const result: BookingQuery = {};

  // Parse ID parameters
  const locationId = parseParam(params, "locationId", isValidId);
  if (locationId) result.locationId = locationId;

  const serviceId = parseParam(params, "serviceId", isValidId);
  if (serviceId) result.serviceId = serviceId;

  // Parse date parameters
  const pickupDate = parseParam(params, "pickupDate", isValidDate);
  if (pickupDate) result.pickupDate = pickupDate;

  const returnDate = parseParam(params, "returnDate", isValidDate);
  if (returnDate) result.returnDate = returnDate;

  // Parse time parameters
  const pickupTime = parseParam(params, "pickupTime", isValidTime);
  if (pickupTime) result.pickupTime = pickupTime;

  const returnTime = parseParam(params, "returnTime", isValidTime);
  if (returnTime) result.returnTime = returnTime;

  // Parse numeric day parameters
  const pickupDays = parseParam(params, "pickupDays", isValidNumericString);
  if (pickupDays) result.pickupDays = pickupDays;

  const returnDays = parseParam(params, "returnDays", isValidNumericString);
  if (returnDays) result.returnDays = returnDays;

  // Parse timezone with fallback
  const timezone = parseParam(params, "timezone", isValidTimezone);
  result.timezone = timezone ?? DEFAULT_TIMEZONE;

  // Parse array parameters
  const receivers = parseArrayParam(params, "receivers", MAX_RECEIVERS, isValidId);
  if (receivers.length > 0) result.receivers = receivers;

  const extras = parseArrayParam(params, "extras", MAX_EXTRAS, isValidExtra);
  if (extras.length > 0) result.extras = extras;

  return result;
}

/**
 * Validate if a value is a numeric string (digits only).
 *
 * @param value - Value to validate
 * @returns true if value is a string containing only digits
 */
function isValidNumericString(value: string): boolean {
  return /^\d+$/.test(value);
}

/**
 * Parse a single query parameter with validation.
 *
 * @param params - URLSearchParams instance
 * @param key - Parameter key to retrieve
 * @param validator - Validation function for the parameter value
 * @returns Validated parameter value or undefined
 */
function parseParam(
  params: URLSearchParams,
  key: string,
  validator: (value: string) => boolean
): string | undefined {
  const value = params.get(key);
  
  if (value === null) {
    return undefined;
  }
  
  return validator(value) ? value : undefined;
}

/**
 * Parse comma-separated array parameter with validation.
 *
 * @param params - URLSearchParams instance
 * @param key - Parameter key to retrieve
 * @param maxLength - Maximum number of items to include
 * @param validator - Validation function for each item
 * @returns Array of valid, trimmed items (up to maxLength)
 */
function parseArrayParam(
  params: URLSearchParams,
  key: string,
  maxLength: number,
  validator: (value: string) => boolean
): string[] {
  const raw = params.get(key);
  
  if (raw === null) {
    return [];
  }

  return raw
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item.length > 0 && validator(item))
    .slice(0, maxLength);
}

/**
 * Merge a partial BookingQuery patch into a base query.
 *
 * @param base - Base BookingQuery object
 * @param patch - Partial updates to merge (undefined values are ignored)
 * @returns New BookingQuery with patch values applied
 *
 * @example
 * ```ts
 * const base = { locationId: "loc-123", timezone: "Europe/Berlin" };
 * const updated = mergeBookingQuery(base, { pickupDate: "2025-01-01" });
 * // { locationId: "loc-123", timezone: "Europe/Berlin", pickupDate: "2025-01-01" }
 * ```
 *
 * @remarks
 * - Creates a new object (does not mutate base)
 * - Undefined values in patch are ignored (won't overwrite base)
 * - Null values are NOT filtered (use undefined to skip fields)
 */
export function mergeBookingQuery(
  base: BookingQuery,
  patch: Partial<BookingQuery>
): BookingQuery {
  const filtered = Object.fromEntries(
    Object.entries(patch).filter(([, value]) => value !== undefined)
  ) as Partial<BookingQuery>;

  return {
    ...base,
    ...filtered,
  };
}

/**
 * Convert a BookingQuery object to a stable, sorted URL query string.
 *
 * @param query - BookingQuery object to serialize
 * @returns URL-encoded query string (without leading '?')
 *
 * @example
 * ```ts
 * const query = { locationId: "loc-123", pickupDate: "2025-12-10" };
 * stringifyBookingQuery(query);
 * // "locationId=loc-123&pickupDate=2025-12-10&timezone=Europe%2FBerlin"
 * ```
 *
 * @remarks
 * - Keys are always in stable order (defined by BOOKING_QUERY_KEYS)
 * - Array values are joined with commas
 * - Null/undefined values are omitted
 * - Values are URL-encoded
 */
export function stringifyBookingQuery(query: BookingQuery): string {
  const params = new URLSearchParams();

  for (const key of BOOKING_QUERY_KEYS) {
    const value = query[key];

    if (value === null || value === undefined) {
      continue;
    }

    if (Array.isArray(value)) {
      if (value.length > 0) {
        params.set(key, value.join(","));
      }
    } else {
      params.set(key, String(value));
    }
  }

  return params.toString();
}

/**
 * Update a query string by parsing, merging changes, and re-stringifying.
 *
 * @param currentSearch - Current URL search string
 * @param patch - Partial updates to merge
 * @returns New query string with updates applied
 *
 * @example
 * ```ts
 * const newSearch = updateBookingQuery(
 *   "?locationId=loc-123",
 *   { pickupDate: "2025-12-10" }
 * );
 * // "locationId=loc-123&pickupDate=2025-12-10&timezone=Europe%2FBerlin"
 * ```
 *
 * @remarks
 * - Performs full round-trip: parse → merge → stringify
 * - Useful for controlled query updates in navigation hooks
 * - Maintains stable key ordering
 */
export function updateBookingQuery(
  currentSearch: string,
  patch: Partial<BookingQuery>
): string {
  const current = parseBookingQuery(currentSearch);
  const merged = mergeBookingQuery(current, patch);
  return stringifyBookingQuery(merged);
}

/**
 * Validate logical consistency of a BookingQuery.
 *
 * @param query - BookingQuery to validate
 * @returns true if logically valid, false otherwise
 *
 * @remarks
 * Can be extended with additional logical rules as needed
 */
export function isBookingQueryLogicallyValid(query: BookingQuery): boolean {
  // Future validations can be added here:
  // - Date not in the past
  // - Maximum booking duration
  // - Service availability constraints
  // etc.

  return true;
}
